import { Plus, Pencil } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import type { Column } from "@/components/shared/data-table";
import { DataTable } from "@/components/shared/data-table";
import { PageHeader } from "@/components/shared/page-header";
import { StatusToggleBadge } from "@/components/shared/status-toggle-badge";
import { Button } from "@/components/ui/button";
import { useHasPermission, useMe } from "@/hooks/use-auth";
import { useUpdateRider } from "@/hooks/use-riders";
import { useServerTable } from "@/hooks/use-server-table";
import { useWorkspaceFilterOption } from "@/hooks/use-workspace-filter";
import { useWorkspaces } from "@/hooks/use-workspaces";
import { queryKeys } from "@/lib/query-keys";
import { riderService } from "@/services/rider";

import { RiderDialog } from "./rider-dialog";

import type { Rider } from "@artisancode/api-types";

export function Riders() {
  const canCreate = useHasPermission("riders.create");
  const canUpdate = useHasPermission("riders.update");

  const { mutate: update } = useUpdateRider();
  const { data: me } = useMe();
  const { data: workspacesData } = useWorkspaces({ per_page: 100 });
  const workspaces = workspacesData?.items ?? [];

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Rider | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const workspaceFilter = useWorkspaceFilterOption();
  const table = useServerTable<Rider>({
    queryKey: (params) => queryKeys.riders.list(params),
    fetcher: (params) => riderService.list(params),
    pageSize: 10,
    filterKeys: ["workspace_id"],
  });

  function toggleActive(item: Rider) {
    if (!canUpdate || updatingId === item.id) return;
    setUpdatingId(item.id);
    update(
      { id: item.id, isActive: !item.isActive },
      {
        onSuccess: () =>
          toast.success(item.isActive ? "Dinonaktifkan." : "Diaktifkan."),
        onError: () => toast.error("Gagal mengubah status rider."),
        onSettled: () => setUpdatingId(null),
      },
    );
  }

  function workspaceName(workspaceId: string) {
    return workspaces.find((w) => w.id === workspaceId)?.name ?? "-";
  }

  const columns: Column<Rider>[] = [
    { key: "name", label: "Nama Rider", render: (r) => r.name },
    ...(me?.canSwitchWorkspace
      ? [
          {
            key: "workspace",
            label: "Workspace",
            render: (r: Rider) => (
              <span className="text-sm">{workspaceName(r.workspaceId)}</span>
            ),
          },
        ]
      : []),
    {
      key: "status",
      label: "Status",
      render: (r) => (
        <StatusToggleBadge
          isActive={r.isActive}
          isLoading={updatingId === r.id}
          disabled={!canUpdate}
          onClick={() => toggleActive(r)}
        />
      ),
    },
  ];

  function openAdd() {
    setEditing(null);
    setOpen(true);
  }

  function openEdit(item: Rider) {
    setEditing(item);
    setOpen(true);
  }

  return (
    <div>
      <PageHeader
        title="Rider"
        action={
          <Button size="sm" onClick={openAdd} disabled={!canCreate}>
            <Plus className="mr-1 h-4 w-4" />
            Tambah
          </Button>
        }
      />
      <DataTable
        data={table.items}
        loadedData={table.loadedItems}
        columns={columns}
        searchPlaceholder="Cari rider..."
        query={table.query}
        onQueryChange={table.onQueryChange}
        filters={workspaceFilter}
        activeFilters={table.activeFilters}
        onFilterChange={table.onFilterChange}
        page={table.page}
        totalPages={table.totalPages}
        totalCount={table.totalCount}
        onPageChange={table.onPageChange}
        hasMore={table.hasMore}
        onLoadMore={table.onLoadMore}
        loading={table.loading}
        actions={(item) => (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => openEdit(item)}
            disabled={!canUpdate}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      />

      <RiderDialog open={open} onOpenChange={setOpen} editing={editing} />
    </div>
  );
}
